// @ts-nocheck
import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';

export interface EventData {
  id: string;
  name: string;
  description: string | null;
  start_date: string | null;
  end_date: string | null;
  venue_name: string | null;
  venue_city: string | null;
  venue_state: string | null;
  registration_type: string | null;
  sport_id: string | null;
  gender: string | null;
  event_type_name: string | null;
  sport_name: string | null;
  thumbnail_url: string | null;
  external_registration_url: string | null;
  promo_video_url: string | null;
  pricing_notes: string | null;
}

interface UseEventsFilters {
  sport?: string;
  gender?: string;
  eventType?: string;
  limit?: number;
}

interface UseCampsFilters {
  sport?: string;
  gender?: string;
  limit?: number;
}

export function useEvents(filters: UseEventsFilters = {}) {
  const [events, setEvents] = useState<EventData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      setError(null);

      let query = supabase
        .from('events')
        .select(`
          id,
          name,
          description,
          start_date,
          end_date,
          venue_name,
          venue_city,
          venue_state,
          registration_type,
          sport_id,
          gender,
          thumbnail_url,
          external_registration_url,
          promo_video_url,
          pricing_notes,
          event_types!event_type_id(name),
          sports!sport_id(name)
        `)
        .eq('is_active', true)
        .eq('is_archived', false)
        .eq('status', 'published')
        .order('start_date', { ascending: true });

      // Filter to show only current/upcoming events (not past)
      // Check end_date if exists, otherwise use start_date
      const today = new Date().toISOString().split('T')[0];
      query = query.or(`end_date.gte.${today},and(end_date.is.null,start_date.gte.${today})`);

      // Filter out camps - only get non-camp events
      query = query.neq('event_types.name', 'Camp');

      // Apply sport filter
      if (filters.sport) {
        query = query.eq('sports.name', filters.sport);
      }

      // Apply gender filter
      if (filters.gender && filters.gender !== 'all') {
        query = query.eq('gender', filters.gender);
      }

      // Apply limit
      if (filters.limit) {
        query = query.limit(filters.limit);
      }

      const { data, error: fetchError } = await query;

      if (fetchError) {
        throw fetchError;
      }

      // Transform the data to match our interface
      const transformedEvents: EventData[] = (data || []).map((event: any) => ({
        id: event.id,
        name: event.name,
        description: event.description,
        start_date: event.start_date,
        end_date: event.end_date,
        venue_name: event.venue_name,
        venue_city: event.venue_city,
        venue_state: event.venue_state,
        registration_type: event.registration_type,
        sport_id: event.sport_id,
        gender: event.gender,
        event_type_name: event.event_types?.name || null,
        sport_name: event.sports?.name || null,
        thumbnail_url: event.thumbnail_url,
        external_registration_url: event.external_registration_url,
        promo_video_url: event.promo_video_url,
        pricing_notes: event.pricing_notes,
      }));

      setEvents(transformedEvents);
    } catch (err: any) {
      console.error('Error fetching events:', err);
      setError(err.message || 'Failed to fetch events');
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, [filters.sport, filters.gender, filters.eventType, filters.limit]);

  return {
    events,
    loading,
    error,
    refetch: fetchEvents,
  };
}

export function useCamps(filters: UseCampsFilters = {}) {
  const [camps, setCamps] = useState<EventData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCamps = async () => {
    try {
      setLoading(true);
      setError(null);

      let query = supabase
        .from('events')
        .select(`
          id,
          name,
          description,
          start_date,
          end_date,
          venue_name,
          venue_city,
          venue_state,
          registration_type,
          sport_id,
          gender,
          thumbnail_url,
          external_registration_url,
          promo_video_url,
          pricing_notes,
          event_types!event_type_id(name),
          sports!sport_id(name)
        `)
        .eq('is_active', true)
        .eq('is_archived', false)
        .eq('status', 'published')
        .eq('event_types.name', 'Camp') // Only camps
        .order('start_date', { ascending: true });

      // Filter to show only current/upcoming camps (not past)
      const today = new Date().toISOString().split('T')[0];
      query = query.or(`end_date.gte.${today},and(end_date.is.null,start_date.gte.${today})`);

      // Apply sport filter
      if (filters.sport) {
        query = query.eq('sports.name', filters.sport);
      }

      // Apply gender filter
      if (filters.gender && filters.gender !== 'all') {
        query = query.eq('gender', filters.gender);
      }

      // Apply limit
      if (filters.limit) {
        query = query.limit(filters.limit);
      }

      const { data, error: fetchError } = await query;

      if (fetchError) {
        throw fetchError;
      }

      // Transform the data to match our interface
      const transformedCamps: EventData[] = (data || []).map((event: any) => ({
        id: event.id,
        name: event.name,
        description: event.description,
        start_date: event.start_date,
        end_date: event.end_date,
        venue_name: event.venue_name,
        venue_city: event.venue_city,
        venue_state: event.venue_state,
        registration_type: event.registration_type,
        sport_id: event.sport_id,
        gender: event.gender,
        event_type_name: event.event_types?.name || null,
        sport_name: event.sports?.name || null,
        thumbnail_url: event.thumbnail_url,
        external_registration_url: event.external_registration_url,
        promo_video_url: event.promo_video_url,
        pricing_notes: event.pricing_notes,
      }));

      setCamps(transformedCamps);
    } catch (err: any) {
      console.error('Error fetching camps:', err);
      setError(err.message || 'Failed to fetch camps');
      setCamps([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCamps();
  }, [filters.sport, filters.gender, filters.limit]);

  return {
    camps,
    loading,
    error,
    refetch: fetchCamps,
  };
}